import WarningOutlined from "@mui/icons-material/WarningOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { getStaffFacingActivityTitle } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityStatus, ActivityType } from "generated-graphql/graphql";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useParams, Link as RouterLink } from "react-router-dom";
import { prettyPrintDateTime } from "../../../../util/dates";
import { useCohortDetails } from "../useCohortDetails";
import { CohortStaffInputs } from "./CohortStaffInputs";
import { CohortActivityBottomToolbar } from "./ActivityBottomToolbar";

// Activities that don't have meaningful display info when successful
const MINIMAL_DISPLAY_ACTIVITIES = [
  ActivityType.ValidateCohort,
  ActivityType.ValidateCohortMembership,
  ActivityType.InitializeCohorts,
  ActivityType.WaitForCohorts,
  ActivityType.WaitForAllCnReports,
  ActivityType.WaitForAllCredentialReports,
  ActivityType.WaitForAllAltIdReports,
  ActivityType.WaitForAllStateReports,
  ActivityType.WaitForAllFd,
  ActivityType.WaitForAllLepc,
];

export function CohortActivityDetails() {
  const theme = useTheme();
  const { cohortId, activityId } = useParams<{
    cohortId: string;
    activityId: string;
  }>();
  const { data } = useCohortDetails();

  const activity = data?.cohort?.currentWorkflow?.activities?.find(
    (a: any) => a.id === activityId
  );

  const { data: logData, loading: loadingActivityLog } = useActivityLogs(
    activity?.id,
    `status:${ActivityStatus.Error}`,
    0,
    1,
    [{ field: "time", sort: "desc" }]
  );

  const recentErrorMessage =
    logData?.activityLogs.items?.[0]?.message ?? "Unknown Error";

  if (!activity) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="text.secondary">
          Activity not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The requested activity could not be found in the current workflow.
        </Typography>
      </Box>
    );
  }

  // Check if this is a minimal display activity that completed successfully
  const shouldShowMinimalDisplay =
    MINIMAL_DISPLAY_ACTIVITIES.includes(activity.type) &&
    (activity.status === ActivityStatus.Completed ||
      activity.status === ActivityStatus.StaffCompleted) &&
    !activity.documents?.length &&
    !activity.payments?.length &&
    !activity.description;

  // Show minimal display for successful validation-type activities
  if (shouldShowMinimalDisplay) {
    return (
      <Paper sx={{ p: 3 }} elevation={0}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {getStaffFacingActivityTitle(activity)}
            </Typography>
            <Chip
              label={prettyPrintEnumValue(activity.status)}
              color="success"
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            This activity completed successfully with no additional details to
            display.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  // Special handling for Distribute Cohort Documents activity
  if (activity.type === ActivityType.DistributeCohortDocuments) {
    return (
      <Paper sx={{ p: 3 }} elevation={0}>
        <Stack spacing={3}>
          {/* Activity Header */}
          <Box>
            <Typography variant="h5" gutterBottom>
              {getStaffFacingActivityTitle(activity)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={prettyPrintEnumValue(activity.status)}
                color={
                  activity.status === ActivityStatus.Completed ||
                  activity.status === ActivityStatus.StaffCompleted
                    ? "success"
                    : activity.status === ActivityStatus.Error
                    ? "error"
                    : activity.status === ActivityStatus.InProgress
                    ? "primary"
                    : activity.status === ActivityStatus.Canceled
                    ? "warning"
                    : "default"
                }
                size="small"
              />
            </Stack>
          </Box>

          {/* Error Message for Distribute Documents */}
          {activity.status === ActivityStatus.Error && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: "error.main",
              }}
            >
              {loadingActivityLog ? (
                <Stack direction="row" gap={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography
                    variant="body1"
                    color="error.main"
                    fontWeight="medium"
                  >
                    Loading error details...
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" gap={1} alignItems="center">
                  <WarningOutlined color="error" />
                  <Typography
                    variant="body1"
                    color="error.main"
                    fontWeight="medium"
                  >
                    Error: {recentErrorMessage}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}

          {/* Link to Documents Tab */}
          <Box>
            <Typography variant="body1" gutterBottom>
              Documents have been distributed to cohort members.
            </Typography>
            <Button
              component={RouterLink}
              to={`/staff/cohorts/${cohortId}/documents`}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              View Distributed Documents
            </Button>
          </Box>

          {/* Show documents if available */}
          {activity.documents && activity.documents.length > 0 && (
            <>
              <Divider />
              <Stack spacing={2}>
                <Typography variant="h6">Documents Distributed</Typography>
                {activity.documents.map((doc: any) => (
                  <Box
                    key={doc.document.id}
                    sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      {doc.document.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Updated: {prettyPrintDateTime(doc.document.updatedAt)}
                      {doc.document.sizeMegabytes &&
                        ` | Size: ${doc.document.sizeMegabytes.toFixed(2)} MB`}
                    </Typography>
                    {doc.document.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {doc.document.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }} elevation={0}>
      <Stack spacing={3}>
        {/* Activity Header */}
        <Box>
          <Typography variant="h5" gutterBottom>
            {getStaffFacingActivityTitle(activity)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Activity ID: {activity.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status:
            </Typography>
            <Chip
              label={prettyPrintEnumValue(activity.status)}
              color={
                activity.status === ActivityStatus.Completed ||
                activity.status === ActivityStatus.StaffCompleted
                  ? "success"
                  : activity.status === ActivityStatus.Error
                  ? "error"
                  : activity.status === ActivityStatus.InProgress
                  ? "primary"
                  : activity.status === ActivityStatus.Canceled
                  ? "warning"
                  : "default"
              }
              size="small"
            />
          </Stack>
        </Box>

        {/* Error Message */}
        {activity.status === ActivityStatus.Error && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: "error.main",
            }}
          >
            {loadingActivityLog ? (
              <Stack direction="row" gap={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography
                  variant="body1"
                  color="error.main"
                  fontWeight="medium"
                >
                  Loading error details...
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" gap={1} alignItems="center">
                <WarningOutlined color="error" />
                <Typography
                  variant="body1"
                  color="error.main"
                  fontWeight="medium"
                >
                  Error: {recentErrorMessage}
                </Typography>
              </Stack>
            )}
          </Box>
        )}

        {/* For Staff activities, show staff-specific inputs */}
        {activity.type === ActivityType.Staff && (
          <CohortStaffInputs activity={activity as any} />
        )}

        {/* Documents Section */}
        {activity.documents && activity.documents.length > 0 && (
          <>
            <Divider />
            <Stack spacing={2}>
              <Typography variant="h6">Documents</Typography>
              {activity.documents.map((doc: any) => (
                <Box
                  key={doc.document.id}
                  sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {doc.document.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Updated: {prettyPrintDateTime(doc.document.updatedAt)}
                    {doc.document.sizeMegabytes &&
                      ` | Size: ${doc.document.sizeMegabytes.toFixed(2)} MB`}
                  </Typography>
                  {doc.document.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {doc.document.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* Payments Section */}
        {activity.payments && activity.payments.length > 0 && (
          <>
            <Divider />
            <Stack spacing={2}>
              <Typography variant="h6">Payments</Typography>
              {activity.payments.map((payment: any) => (
                <Box
                  key={payment.id}
                  sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Amount: ${payment.amount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Status: {payment.status}
                    {payment.paidAt &&
                      ` | Paid: ${prettyPrintDateTime(payment.paidAt)}`}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* Activity Bottom Toolbar with Complete Activity Button */}
        <CohortActivityBottomToolbar activity={activity as any} />
      </Stack>
    </Paper>
  );
}
